// External Imports
import { FieldErrors, FormProvider, useFieldArray } from 'react-hook-form'
import { SaveBar } from '@shopify/app-bridge-react'
import { Card, EmptyState, Layout } from '@shopify/polaris'

// Custom Hooks
import useScrollToError from '@/hooks/useScrollToError'

// Form Imports
import { useOverrideForm } from './hooks'
import { type FormSchema } from './schema'

// Component Imports
import { OverrideGroups } from '@/components/overrides/OverrideGroups'
import { OVERRIDE_TEMPLATE } from '@/common/constants'

import { Override } from '@/common/types'
import { openBeacon } from '@/common/functions'

const OverrideForm = ({ overrides }: { overrides: Override[] }) => {
  const { submit, isPending, form } = useOverrideForm(overrides)

  const {
    formState: { isDirty },
    handleSubmit,
    reset,
    control,
  } = form

  const overrideArray = useFieldArray({
    control,
    name: `overrides`,
  })

  const { fields, append } = overrideArray

  const [setCanFocus] = useScrollToError(form.formState.errors)

  const onFormSubmitValid = (data: FormSchema) => {
    submit(data)
  }

  const onFormSubmitInvalid = (errors: FieldErrors<FormSchema>) => {
    setCanFocus(true)
    console.log(errors)
  }

  const addOverride = () => {
    append(OVERRIDE_TEMPLATE)
  }

  return (
    <FormProvider {...form}>
      <SaveBar id="override-save-bar" open={isDirty}>
        <button
          variant="primary"
          onClick={() => handleSubmit(onFormSubmitValid, onFormSubmitInvalid)()}
          disabled={isPending}
          loading={isPending ? '' : undefined}
        ></button>
        <button onClick={() => reset()} disabled={isPending}></button>
      </SaveBar>

      {fields.length <= 0 ? (
        <Layout.Section>
          <Card>
            <EmptyState
              heading="Override settings for individual products"
              action={{
                content: 'Get started',
                onAction: addOverride,
              }}
              secondaryAction={{
                content: 'Contact support',
                onAction: () => {
                  openBeacon('ASK')
                },
              }}
              image="emptystate-files.png"
            >
              <p>
                Product overrides allow you to override specific settings, or
                hide Bloom altogether, but only for desired products.
              </p>
            </EmptyState>
          </Card>
        </Layout.Section>
      ) : (
        <OverrideGroups fieldArray={overrideArray} />
      )}
    </FormProvider>
  )
}

export { OverrideForm }
